import { useEffect, useRef } from 'react';

import { Box } from '@mui/material';

interface RichHtmlFrameProps {
  html: string;
}

/**
 * Affiche du HTML riche (document complet) dans une iframe isolée.
 * Permet de coller du HTML généré par IA (avec style, structure, etc.)
 * sans conflit avec les styles de la page parente.
 */
const RichHtmlFrame = ({ html }: RichHtmlFrameProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resizeToContent = () => {
      try {
        const doc = iframe.contentDocument;
        if (doc?.body) {
          const height = Math.max(doc.body.scrollHeight, doc.documentElement?.scrollHeight ?? 0);
          iframe.style.height = `${height + 24}px`;
        }
      } catch {
        // Sandbox ou cross-origin peut bloquer l'accès
      }
    };

    iframe.onload = resizeToContent;

    // Fallback si onload ne suffit pas (styles chargés en différé)
    const timeoutId = setTimeout(resizeToContent, 500);

    return () => clearTimeout(timeoutId);
  }, [html]);

  return (
    <Box sx={{ width: '100%', minHeight: 200 }}>
      <iframe
        ref={iframeRef}
        srcDoc={html}
        title="Contenu HTML"
        sandbox="allow-same-origin"
        style={{
          width: '100%',
          minHeight: 200,
          border: 'none',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default RichHtmlFrame;
